import { useState, useEffect, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import { Button } from "../../components/ui/button";
import { RefreshCw, Download, ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { AUDIT_LOG_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

// ── Constants ─────────────────────────────────────────────────────────────────

const ENTITY_TYPES = [
    { value: '', label: 'All Types' },
    { value: 'customer',  label: 'Customer' },
    { value: 'auth',      label: 'Auth' },
    { value: 'whatsapp',  label: 'WhatsApp' },
    { value: 'email',     label: 'Email' },
    { value: 'settings',  label: 'Settings' },
];

const ACTIONS = [
    { value: '',              label: 'All Actions' },
    { value: 'create',        label: 'Create' },
    { value: 'update',        label: 'Update' },
    { value: 'delete',        label: 'Delete' },
    { value: 'status_change', label: 'Status Change' },
    { value: 'login',         label: 'Login' },
    { value: 'login_failed',  label: 'Login Failed' },
    { value: 'user_create',   label: 'User Create' },
    { value: 'user_update',   label: 'User Update' },
    { value: 'user_delete',   label: 'User Delete' },
    { value: 'role_create',   label: 'Role Create' },
    { value: 'role_update',   label: 'Role Update' },
    { value: 'role_delete',   label: 'Role Delete' },
    { value: 'connect',       label: 'Connect' },
    { value: 'send',          label: 'Send' },
    { value: 'campaign_create', label: 'Campaign Create' },
    { value: 'campaign_send',   label: 'Campaign Send' },
];

const ENTITY_BADGE = {
    customer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    auth:     'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    whatsapp: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    email:    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    settings: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const ACTION_BADGE = {
    create:         'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    update:         'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    delete:         'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    status_change:  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    login:          'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
    login_failed:   'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    user_create:    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    user_update:    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    user_delete:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    role_create:    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    role_update:    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    role_delete:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    connect:        'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
    send:           'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    campaign_create:'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    campaign_send:  'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
};

// ── Details expander ─────────────────────────────────────────────────────────

const DetailsCell = ({ details }) => {
    const [open, setOpen] = useState(false);
    if (!details || !Object.keys(details).length) return <span className="text-gray-400 text-xs">—</span>;
    return (
        <div>
            <button
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700"
            >
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {open ? 'hide' : 'show'}
            </button>
            {open && (
                <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 overflow-auto max-w-xs">
                    {JSON.stringify(details, null, 2)}
                </pre>
            )}
        </div>
    );
};

// ── CSV export helper ─────────────────────────────────────────────────────────

const exportCSV = (rows) => {
    const headers = ['ID', 'Date/Time', 'User', 'Action', 'Entity Type', 'Entity Name', 'Entity ID', 'IP Address', 'Details'];
    const lines = rows.map(r => [
        r.id,
        dayjs(r.created_date).format('YYYY-MM-DD HH:mm:ss'),
        r.user_name || '',
        r.action,
        r.entity_type,
        r.entity_name || '',
        r.entity_id || '',
        r.ip_address || '',
        JSON.stringify(r.details || {}),
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
};

// PAGE_SIZE is now a state variable (see component)

// ── Main component ─────────────────────────────────────────────────────────────

const AuditLogReport = () => {
    const { fetch } = useApiMethods();

    const [rows, setRows]       = useState([]);
    const [total, setTotal]     = useState(0);
    const [loading, setLoading] = useState(false);

    // Filters
    const [fromDate, setFromDate]     = useState('');
    const [toDate, setToDate]         = useState('');
    const [entityType, setEntityType] = useState('');
    const [action, setAction]         = useState('');
    const [page, setPage]             = useState(1);
    const [pageSize, setPageSize]     = useState(50);

    const load = useCallback((currentPage = 1) => {
        // Don't fetch with only one date filled
        if (fromDate && !toDate) return;
        if (!fromDate && toDate) return;
        setLoading(true);
        const p = new URLSearchParams({ page: currentPage, page_size: pageSize });
        if (entityType) p.set('entity_type', entityType);
        if (action)     p.set('action', action);
        if (fromDate)   p.set('from_date', fromDate);
        if (toDate)     p.set('to_date', toDate);
        fetch(`${AUDIT_LOG_URL}?${p.toString()}`)
            .then(res => {
                if (res?.status === 'success') {
                    setRows(res.data || []);
                    setTotal(res.total || 0);
                } else {
                    ToastMsg('error', res?.message || 'Failed to load audit log');
                }
            })
            .catch(() => ToastMsg('error', 'Failed to load audit log'))
            .finally(() => setLoading(false));
    }, [entityType, action, fromDate, toDate, pageSize]);

    useEffect(() => { setPage(1); load(1); }, [load]);

    const handleClear = () => {
        setFromDate('');
        setToDate('');
        setEntityType('');
        setAction('');
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        load(newPage);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <ContainerCard className="p-4 md:p-6">
            {/* ── fixed top: header + filters ── */}
            <div className="flex-shrink-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Audit Log</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {total} record{total !== 1 ? 's' : ''} found
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => load(page)} disabled={loading}>
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        <span className="ml-1 hidden sm:inline">Refresh</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportCSV(rows)} disabled={!rows.length}>
                        <Download size={14} />
                        <span className="ml-1 hidden sm:inline">Export CSV</span>
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                    <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                </div>

                <select
                    value={entityType}
                    onChange={e => setEntityType(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                    {ENTITY_TYPES.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>

                <select
                    value={action}
                    onChange={e => setAction(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                    {ACTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>

                <Button size="sm" variant="outline" onClick={handleClear} disabled={loading}>
                    Clear
                </Button>
            </div>
            </div>

            {/* ── scrollable table + fixed pagination ── */}
            <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Table */}
                <div className="flex-1 min-h-0 overflow-auto">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs uppercase">
                        <tr>
                            <th className="px-3 py-3 text-left whitespace-nowrap">Date / Time</th>
                            <th className="px-3 py-3 text-left">User</th>
                            <th className="px-3 py-3 text-left">Action</th>
                            <th className="px-3 py-3 text-left">Type</th>
                            <th className="px-3 py-3 text-left">Entity</th>
                            <th className="px-3 py-3 text-left">IP</th>
                            <th className="px-3 py-3 text-left">Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {loading && (
                            <tr>
                                <td colSpan={7} className="text-center py-8 text-gray-400">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center py-8 text-gray-400">
                                    No audit records found
                                </td>
                            </tr>
                        )}
                        {!loading && rows.map(row => (
                            <tr
                                key={row.id}
                                className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                            >
                                <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-400 text-xs">
                                    {dayjs(row.created_date).format('DD MMM YYYY HH:mm:ss')}
                                </td>
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200 font-medium">
                                    {row.user_name || <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_BADGE[row.action] || 'bg-gray-100 text-gray-600'}`}>
                                        {row.action}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ENTITY_BADGE[row.entity_type] || 'bg-gray-100 text-gray-600'}`}>
                                        {row.entity_type}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                    {row.entity_name || <span className="text-gray-400">—</span>}
                                    {row.entity_id && (
                                        <span className="ml-1 text-xs text-gray-400">#{row.entity_id}</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">
                                    {row.ip_address || '—'}
                                </td>
                                <td className="px-3 py-2">
                                    <DetailsCell details={row.details} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>

            {/* Pagination */}
            <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                    <select
                        value={pageSize}
                        onChange={e => handlePageSizeChange(Number(e.target.value))}
                        className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                    >
                        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {total === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} of {total}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => handlePageChange(1)} disabled={page === 1 || loading} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                    <button onClick={() => handlePageChange(page - 1)} disabled={page === 1 || loading} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                        .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                        .map((p, i) => p === '…'
                            ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                            : <button key={p} onClick={() => handlePageChange(p)} disabled={loading} className={cn("min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors", p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700")}>{p}</button>
                        )
                    }
                    <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages || loading} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                    <button onClick={() => handlePageChange(totalPages)} disabled={page === totalPages || loading} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
                </div>
            </div>
            </div>
        </ContainerCard>
    );
};

export default AuditLogReport;
